/**
 * Member Activation → Modules (AR-328).
 *
 * The builder shell: filters, the drag-and-drop module/step list, and the
 * module/step editor panels. Design/layout only — the tree is the fixture in
 * lib/activationBuilder, so reorders and archives live in local state until the
 * module/step APIs land.
 */
import React, { useMemo, useState } from 'react';
import { ChevronLeft, Plus } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import ActivationFilters, {
  type ActivationFilterState,
} from '@/components/admin/activation/ActivationFilters';
import ActivationModuleList from '@/components/admin/activation/ActivationModuleList';
import ActivationModuleEditor from '@/components/admin/activation/ActivationModuleEditor';
import ActivationStepEditor from '@/components/admin/activation/ActivationStepEditor';
import AddStepMenu from '@/components/admin/activation/AddStepMenu';
import {
  ACTIVATION_MODULES,
  exampleStepForType,
  findActivationModule,
  findActivationStep,
  type ActivationModule,
  type ActivationStep,
  type FilterGroupId,
  type StepTypeOption,
} from '@/lib/activationBuilder';

const applyFilters = (
  modules: ActivationModule[],
  filters: ActivationFilterState,
): ActivationModule[] => {
  const types = filters.stepType ?? [];
  const routes = filters.route ?? [];
  const pathways = filters.pathway ?? [];
  const requirements = filters.requirement ?? [];
  const moduleIds = filters.module ?? [];
  const states = filters.state ?? [];

  // Every fixture row is active, so an Archived-only filter empties the list.
  if (states.length === 1 && states[0] === 'archived') return [];

  const stepMatches = (step: ActivationStep): boolean => {
    // `Form` is one filter option covering both Form and Form — AI.
    if (types.length && !types.some((t) => step.type.startsWith(t))) return false;
    if (routes.length && !step.routes.some((code) => routes.includes(code))) return false;
    // A step with no pathway scope is shown on every pathway.
    if (pathways.length && step.pathways && !step.pathways.some((p) => pathways.includes(p))) {
      return false;
    }
    if (requirements.length && !requirements.includes(step.requirement)) return false;
    return true;
  };

  const stepLevelActive = !!(
    types.length ||
    routes.length ||
    pathways.length ||
    requirements.length
  );

  return modules
    .filter((module) => !moduleIds.length || moduleIds.includes(module.id))
    .map((module) => ({ ...module, steps: module.steps.filter(stepMatches) }))
    .filter((module) => !stepLevelActive || module.steps.length > 0);
};

const MemberActivationModulesPage: React.FC = () => {
  const { toast } = useToast();
  const [modules, setModules] = useState<ActivationModule[]>(ACTIVATION_MODULES);
  const [expandedModuleIds, setExpandedModuleIds] = useState<string[]>(['activation']);
  const [selectedStepKey, setSelectedStepKey] = useState<string | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [newStep, setNewStep] = useState<ActivationStep | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<ActivationFilterState>({});

  const filtersActive = Object.values(filters).some((list) => (list?.length ?? 0) > 0);
  const visibleModules = useMemo(() => applyFilters(modules, filters), [modules, filters]);

  const editingStep = newStep ?? findActivationStep(modules, selectedStepKey);
  const editingModule = newStep ? null : findActivationModule(modules, selectedModuleId);
  const editing = !!editingStep || !!editingModule;

  const closeEditor = () => {
    setSelectedStepKey(null);
    setSelectedModuleId(null);
    setNewStep(null);
  };

  const handleToggleModule = (moduleId: string) =>
    setExpandedModuleIds((current) =>
      current.includes(moduleId)
        ? current.filter((id) => id !== moduleId)
        : [...current, moduleId],
    );

  const handlePickStepType = (type: StepTypeOption['type']) => {
    setSelectedStepKey(null);
    setSelectedModuleId(null);
    setNewStep(exampleStepForType(type));
  };

  const handleAddStepToModule = (moduleId: string) => {
    const module = findActivationModule(modules, moduleId);
    setSelectedStepKey(null);
    setSelectedModuleId(null);
    setNewStep({ ...exampleStepForType('Simple'), moduleId, moduleName: module?.name ?? 'Activation' });
  };

  const handleArchiveStep = (stepKey: string) => {
    const step = findActivationStep(modules, stepKey);
    setModules((current) =>
      current.map((module) => ({
        ...module,
        steps: module.steps.filter((s) => s.key !== stepKey),
      })),
    );
    closeEditor();
    toast({
      title: 'Step archived',
      description: `“${step?.name ?? 'Step'}” moved to Archive and is excluded from the live flow.`,
    });
  };

  const handleArchiveModule = (moduleId: string) => {
    const module = findActivationModule(modules, moduleId);
    setModules((current) =>
      current.filter((m) => m.id !== moduleId).map((m, index) => ({ ...m, order: index + 1 })),
    );
    closeEditor();
    toast({
      title: 'Module archived',
      description: `“${module?.name ?? 'Module'}” and its steps moved to Archive.`,
    });
  };

  const handleToggleFilterOption = (groupId: FilterGroupId, optionId: string) =>
    setFilters((current) => {
      const list = current[groupId] ?? [];
      return {
        ...current,
        [groupId]: list.includes(optionId)
          ? list.filter((o) => o !== optionId)
          : [...list, optionId],
      };
    });

  return (
    <AdminLayout
      title={editing ? editingStep?.name ?? editingModule?.name ?? 'Modules' : 'Modules'}
      description={
        editing
          ? `Configure this ${editingStep ? 'step' : 'module'}, then save it to the member activation flow.`
          : 'Build and configure member activation — modules and steps together.'
      }
    >
      {editing ? (
        <div className="space-y-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={closeEditor}
            className="h-7 px-2 text-xs text-orange-600 dark:text-orange-400"
          >
            <ChevronLeft className="mr-1 h-3 w-3" /> Back to Modules
          </Button>

          {editingStep ? (
            <ActivationStepEditor
              key={editingStep.key}
              step={editingStep}
              modules={modules}
              onCancel={closeEditor}
              onSave={() => {
                closeEditor();
                toast({
                  title: 'Step saved',
                  description: 'Design preview — step configuration is not persisted yet.',
                });
              }}
              onArchive={() => handleArchiveStep(editingStep.key)}
            />
          ) : (
            editingModule && (
              <ActivationModuleEditor
                key={editingModule.id}
                module={editingModule}
                onCancel={closeEditor}
                onAddStep={() => handleAddStepToModule(editingModule.id)}
                onSave={() => {
                  closeEditor();
                  toast({
                    title: 'Module saved',
                    description: 'Design preview — module configuration is not persisted yet.',
                  });
                }}
                onArchive={() => handleArchiveModule(editingModule.id)}
              />
            )
          )}
        </div>
      ) : (
        <>
          <ActivationFilters
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            selected={filters}
            onToggleOption={handleToggleFilterOption}
            onClear={() => setFilters({})}
            actions={
              <>
                <Button variant="outline" size="sm">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add module
                </Button>
                <AddStepMenu onPick={handlePickStepType} />
              </>
            }
          />

          <ActivationModuleList
            modules={visibleModules}
            expandedModuleIds={expandedModuleIds}
            selectedStepKey={selectedStepKey}
            onToggleModule={handleToggleModule}
            onSelectModule={setSelectedModuleId}
            onSelectStep={setSelectedStepKey}
            onAddStep={handleAddStepToModule}
            onReorder={setModules}
            onArchiveStep={handleArchiveStep}
            dragDisabled={filtersActive}
          />

          {filtersActive && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Reordering is paused while filters are applied — clear the filters to drag modules and
              steps.
            </p>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default MemberActivationModulesPage;
